import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import moment from "moment";

import Loading from "@/components/Loading";
import EmptyData from "@/components/EmptyData";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";

import {PlayerValuationAPI} from "@/api/player_valuation";

import {
	baseExternMatchParameters,
	baseExternTrainingParameters,
	dateFormat,
	initialBaseValuationParameters,
	initialValuationParameters,
	proExternMatchParameters,
	proExternTrainingParameters,
} from "@/utils/constants";
import {getAlertSeverity, getValuationType} from "@/utils/functions";

import "./index.scss";

function PublicValuationPage() {
	const {id} = useParams();
	const {t} = useTranslation();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [valuationData, setValuationData] = useState(null);

	useEffect(() => {
		const load = async () => {
			try {
				const response = await PlayerValuationAPI.getPublic(id);
				setValuationData(response);
			} catch {
				setError(true);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [id]);

	// Determina se è una valutazione esterna (scouting) dal tipo
	const isExternal = valuationData
		? valuationData.valuationType !== 1 && valuationData.valuationType != null
		: true;

	const currentPlayerData = valuationData?.player;

	// ── Configurazione sezioni ──────────────────────────────────────────────────

	const reportConfigurationSize = "col-6 col-xl-4";
	const reportConfiguration = valuationData
		? [
				{
					value: valuationData?.valuationDate
						? moment(valuationData.valuationDate).format(dateFormat)
						: null,
					label: "Data",
					size: reportConfigurationSize,
				},
				{
					value: valuationData?.valuationAuthor,
					label: "Allenatore",
					size: reportConfigurationSize,
				},
			]
		: [];

	if (valuationData && isExternal) {
		reportConfiguration.push(
			{
				value: getValuationType(valuationData.valuationType),
				label: "Tipologia",
				size: reportConfigurationSize,
			},
			{
				value: valuationData?.club?.name,
				label: "Squadra",
				size: reportConfigurationSize,
			},
			{
				value: valuationData?.category?.name,
				label: "Categoria",
				size: reportConfigurationSize,
			},
		);
	} else if (valuationData) {
		reportConfiguration.push({
			value: valuationData?.momentOfGrowth,
			label: "Momento di crescita",
			size: reportConfigurationSize,
		});
	}

	const playerReportConfigurationSize = "col-6 col-xl-3";
	const playerReportConfiguration = valuationData
		? [
				{
					value: currentPlayerData
						? currentPlayerData.firstName + " " + currentPlayerData.lastName
						: "---",
					label: "Giocatore",
					size: playerReportConfigurationSize,
				},
				{
					value: !isExternal
						? currentPlayerData?.category
						: currentPlayerData?.dateOfBirth
							? moment(currentPlayerData.dateOfBirth).format(dateFormat)
							: "---",
					label: !isExternal ? "Categoria" : "Data di nascita",
					size: playerReportConfigurationSize,
				},
				{
					value: currentPlayerData?.goalkeeper
						? "Portiere"
						: currentPlayerData?.primaryRole,
					label: "Ruolo",
					size: playerReportConfigurationSize,
				},
				{
					value: valuationData?.height ? valuationData.height + " cm" : "",
					label: "Altezza",
					size: playerReportConfigurationSize,
				},
				{
					value: valuationData?.weight ? valuationData.weight + " kg" : "",
					label: "Peso",
					size: playerReportConfigurationSize,
				},
			]
		: [];

	if (valuationData && !isExternal) {
		playerReportConfiguration.push({
			value: valuationData?.timeInsideClub,
			label: "Nel club dal",
			size: playerReportConfigurationSize,
		});
	}

	// ── Parametri ───────────────────────────────────────────────────────────────

	let parameters = [];
	if (valuationData && isExternal) {
		if (valuationData.valuationType && valuationData.category) {
			if (!valuationData.category?.basicActivities) {
				if (valuationData.valuationType === 2)
					parameters = proExternMatchParameters(t);
				if (valuationData.valuationType === 3)
					parameters = proExternTrainingParameters(t);
			} else {
				if (valuationData.valuationType === 4)
					parameters = baseExternMatchParameters(t);
				if (valuationData.valuationType === 5)
					parameters = baseExternTrainingParameters(t);
			}
		}
	} else if (valuationData && !isExternal) {
		parameters = (valuationData.category?.basicActivities
			? initialBaseValuationParameters(t)
			: initialValuationParameters(t)
		)?.filter(
			(p) =>
				!p.roles ||
				p.roles.indexOf(currentPlayerData?.primaryRole?.toLowerCase()) !== -1,
		);
	}

	// ── Dettagli ────────────────────────────────────────────────────────────────

	const valuationTextItem = valuationData
		? {
				value: (
					<div className="area-rating-container only-text">
						{valuationData?.valuationText ? (
							<div className="rating-notes big">
								{valuationData.valuationText}
							</div>
						) : (
							<EmptyData />
						)}
					</div>
				),
				label: isExternal ? "Relazione generale" : "Valutazione iniziale",
				size: "col-12",
			}
		: null;

	const reportDetailsConfiguration = parameters.map((p) => ({
		value: (
			<div className="area-rating-container">
				{valuationData[p.fieldKeyRating] && (
					<div className="stat-circle-container">
						<div className="stat-container">
							<div className="value">
								{+parseFloat(valuationData[p.fieldKeyRating]).toFixed(2)}
							</div>
						</div>
					</div>
				)}
				{valuationData?.[p.fieldKey] && (
					<div className="rating-notes">{valuationData[p.fieldKey]}</div>
				)}
				{!valuationData?.[p.fieldKeyRating] && !valuationData?.[p.fieldKey] && (
					<EmptyData />
				)}
			</div>
		),
		label: p.label,
		size: "col-12 col-sm-6",
	}));

	if (valuationData) {
		if (!isExternal && valuationTextItem) {
			reportDetailsConfiguration.push(valuationTextItem);
		}
		if (!isExternal) {
			reportDetailsConfiguration.push({
				value: (
					<div className="area-rating-container only-text">
						{valuationData?.strengthAreas ? (
							<div className="rating-notes">{valuationData.strengthAreas}</div>
						) : (
							<EmptyData />
						)}
					</div>
				),
				label: "Punti di forza, aree migliorabili",
				size: "col-12 col-sm-6",
			});
		}
		if (valuationData.valuationType !== 6) {
			reportDetailsConfiguration.push({
				value: (
					<div className="area-rating-container only-text">
						{valuationData?.finalOverallValuation ? (
							<div className="rating-notes">
								{valuationData.finalOverallValuation}
							</div>
						) : (
							<EmptyData />
						)}
					</div>
				),
				label: "Valutazione complessiva finale",
				size: "col-12 col-sm-6",
			});
		}
		if (isExternal && valuationTextItem) {
			reportDetailsConfiguration.push(valuationTextItem);
		}
	}

	// ── Render ──────────────────────────────────────────────────────────────────

	return (
		<div className="public-valuation-page">
			<div className="public-valuation-page__header">
				<span className="public-valuation-page__brand">Football Youth Manager</span>
				<span className="public-valuation-page__tag">Valutazione</span>
			</div>

			<div className="public-valuation-page__body">
				{loading && <Loading visible />}

				{!loading && error && (
					<div className="public-valuation-page__error">
						<div className="public-valuation-page__error-icon">⚠</div>
						<div className="public-valuation-page__error-title">
							Valutazione non trovata
						</div>
						<div className="public-valuation-page__error-sub">
							Il link potrebbe essere scaduto o non valido.
						</div>
					</div>
				)}

				{!loading && !error && valuationData && (
					<div className="sheet-container page-container">
						{!isExternal ? (
							<>
								<div className="hv-player-banner">
									<div className="hv-player-banner__name">
										{currentPlayerData
											? `${currentPlayerData.firstName} ${currentPlayerData.lastName}`
											: "—"}
									</div>
									<div className="hv-player-banner__meta">
										<span>
											{currentPlayerData?.goalkeeper
												? "Portiere"
												: currentPlayerData?.primaryRole || "—"}
										</span>
										<span className="hv-player-banner__dot">·</span>
										<span>{currentPlayerData?.category || "—"}</span>
									</div>
								</div>

								<div className="vd-meta-strip">
									<div className="vd-meta-strip__field">
										<span className="vd-meta-strip__label">Data</span>
										<span className="vd-meta-strip__value">
											{valuationData?.valuationDate
												? moment(valuationData.valuationDate).format(dateFormat)
												: "—"}
										</span>
									</div>
									<div className="vd-meta-strip__field">
										<span className="vd-meta-strip__label">Allenatore</span>
										<span className="vd-meta-strip__value">
											{valuationData?.valuationAuthor || "—"}
										</span>
									</div>
									{valuationData?.height ? (
										<div className="vd-meta-strip__field">
											<span className="vd-meta-strip__label">Altezza</span>
											<span className="vd-meta-strip__value">
												{valuationData.height} cm
											</span>
										</div>
									) : null}
									{valuationData?.weight ? (
										<div className="vd-meta-strip__field">
											<span className="vd-meta-strip__label">Peso</span>
											<span className="vd-meta-strip__value">
												{valuationData.weight} kg
											</span>
										</div>
									) : null}
									{valuationData?.momentOfGrowth ? (
										<div className="vd-meta-strip__field">
											<span className="vd-meta-strip__label">
												Momento di crescita
											</span>
											<span className="vd-meta-strip__value">
												{valuationData.momentOfGrowth}
											</span>
										</div>
									) : null}
									{valuationData?.valuationRating ? (
										<div className="vd-meta-strip__field vd-meta-strip__field--score">
											<span className="vd-meta-strip__label">Punteggio</span>
											<span className="vd-meta-strip__score">
												{valuationData.valuationRating}
											</span>
										</div>
									) : null}
								</div>
							</>
						) : (
							<>
								<div className="row no-margin col-container">
									<div className="col-sm-12 padding-small">
										<VLFCard
											title="Informazioni valutazione"
											content={
												<div className="row">
													<div className="col-sm-12 col-lg-10">
														<ViewField
															configuration={reportConfiguration}
														/>
													</div>
													<div className="col-sm-12 col-lg-2">
														<div
															className="d-flex flex-column align-items-center gap-2 h-100"
															style={{borderLeft: "1px solid #ebf1fd"}}
														>
															{valuationData.valuationType !== 6 ? (
																<div className="view-field-container avg h-100 justify-content-center">
																	<div className="field-label-container justify-content-center">
																		<div className="field-label">
																			Voto finale
																		</div>
																	</div>
																	<div className="stat-circle-container">
																		<div className="stat-container">
																			<div className="value">
																				{+parseFloat(
																					valuationData?.finalAverage || 0,
																				).toFixed(2)}
																			</div>
																		</div>
																	</div>
																</div>
															) : null}
															<div className="view-field-container avg h-100 justify-content-center">
																<div className="field-label-container justify-content-center">
																	<div className="field-label">Esito</div>
																</div>
																<div className="d-flex justify-content-center">
																	<div
																		style={{maxWidth: "150px"}}
																		className={
																			"severity " +
																			(getAlertSeverity(
																				valuationData?.finalOverallRating,
																				true,
																			)?.severity || "")
																		}
																	>
																		{
																			getAlertSeverity(
																				valuationData?.finalOverallRating,
																				true,
																			)?.message
																		}
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											}
											openable={false}
										/>
									</div>
								</div>
								<div className="row no-margin mt-2">
									<div className="col-sm-12 padding-small">
										<VLFCard
											title="Informazioni giocatore"
											content={
												<div className="row">
													<div className="col-sm-12">
														<ViewField
															configuration={playerReportConfiguration}
														/>
													</div>
												</div>
											}
										/>
									</div>
								</div>
							</>
						)}

						<div className="row no-margin mt-2">
							<div className="col-sm-12 padding-small">
								<VLFCard
									title="Dettagli"
									content={
										<div className="row">
											<div className="col-sm-12">
												<ViewField
													configuration={reportDetailsConfiguration}
												/>
											</div>
										</div>
									}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default PublicValuationPage;
